import React, { useContext, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../components/AuthContext';
import { useWishlist } from './WishlistContext';
import SearchBar from './SearchBar';
import { FaRegHeart, FaRegUser, FaBars, FaTimes } from 'react-icons/fa';
import API from '../api';

function Header() {
  const { user, login, logout } = useContext(AuthContext);
  const { wishlist } = useWishlist();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => setMenuOpen(!isMenuOpen);
  const closeMenu = () => setMenuOpen(false);
  const accountLink = user ? '/account' : '/login';

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 150); // Delay di 150ms prima di chiudere
  };

  const handleAdminClick = async (e) => {
    if (user && user.impersonatedBy) {
      e.preventDefault();
      try {
        const res = await API.post('/auth/admin/return');
        if (res.data.success) {
          login(res.data.user, res.data.token);
          navigate('/admin/vendors');
        }
      } catch (err) {
        alert('Errore nel ritorno all\'admin');
      }
    }
  };

  // Applica la classe homepage-header solo in homepage
  const isHomepage = location.pathname === '/';

  return (
    <header className={`header${isHomepage ? ' homepage-header' : ''}`}>
      <div className="header-inner">
        {/* DESKTOP HEADER */}
        <div className="header-desktop">
          {/* Logo */}
          <div className="header-logo">
            <Link to="/">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Logo Loverlist"
                className="logo-img"
              />
            </Link>
          </div>
          {/* Menu */}
          <nav className="header-menu">
            <Link to="/" className="header-menu-link">Home</Link>
            <Link to="/seller" className="header-menu-link">Negozi</Link>
            <Link to="/categories" className="header-menu-link">Categorie</Link>
            <Link to="/brands" className="header-menu-link">Brand</Link>
          </nav>
          {/* Searchbar (solo se non homepage) */}
          {!isHomepage && (
            <div className="header-searchbar">
              <SearchBar />
            </div>
          )}
          {/* Icone wishlist/account */}
          <div className="header-icons">
            <Link to="/wishlist" className="header-icon header-wishlist-icon" aria-label="Wishlist">
              <FaRegHeart size={24} />
              {wishlist.length > 0 && (
                <span className="header-wishlist-badge">{wishlist.length}</span>
              )}
            </Link>
            {user ? (
              <div
                className={`header-dropdown ${isDropdownOpen ? 'show' : ''}`}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
              >
                <button
                  className="header-account-btn"
                  id="accountDropdown"
                  aria-expanded={isDropdownOpen}
                  aria-label="Account"
                >
                  <FaRegUser size={24} />
                </button>
                <ul
                  className={`header-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
                  aria-labelledby="accountDropdown"
                >
                  <li>
                    <Link className="header-dropdown-item" to="/account">
                      Profilo
                    </Link>
                  </li>
                  {user && (user.role === 'admin' || user.impersonatedBy?.role === 'admin') && (
                    <li>
                      <Link className="header-dropdown-item" to="/admin/vendors" onClick={handleAdminClick}>
                        Admin
                      </Link>
                    </li>
                  )}
                  <li>
                    <button className="header-dropdown-item" onClick={logout}>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link to={accountLink} className="header-icon" aria-label="Account">
                <FaRegUser size={24} />
              </Link>
            )}
          </div>
        </div>

        {/* MOBILE/TABLET HEADER */}
        <div className="header-mobile">
          {/* Top row: hamburger, logo, icone */}
          <div className="header-mobile-top">
            <button className="header-hamburger" onClick={toggleMenu} aria-label="Menu">
              <FaBars size={24} />
            </button>
            <div className="header-logo">
              <Link to="/">
                <img
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="Logo Loverlist"
                  className="logo-img"
                />
              </Link>
            </div>
            <div className="header-icons">
              <Link to="/wishlist" className="header-icon header-wishlist-icon" aria-label="Wishlist">
                <FaRegHeart size={24} />
                {wishlist.length > 0 && (
                  <span className="header-wishlist-badge">{wishlist.length}</span>
                )}
              </Link>
              {user ? (
                <button
                  className="header-account-btn"
                  onClick={logout}
                  aria-label="Account"
                >
                  <FaRegUser size={24} />
                </button>
              ) : (
                <Link to={accountLink} className="header-icon" aria-label="Account">
                  <FaRegUser size={24} />
                </Link>
              )}
            </div>
          </div>
          {/* Searchbar sotto (solo se non homepage) */}
          {!isHomepage && (
            <div className="header-mobile-searchbar">
              <SearchBar />
            </div>
          )}
        </div>

        {/* MOBILE MENU OVERLAY */}
        {isMenuOpen && (
          <div className="header-mobile-menu-overlay" onClick={closeMenu} />
        )}
        
        {/* MOBILE MENU SIDEBAR */}
        <div className={`header-mobile-menu-sidebar ${isMenuOpen ? 'show' : ''}`}>
          <div className="header-mobile-menu-header">
            <button className="header-mobile-close-btn" onClick={closeMenu} aria-label="Chiudi menu">
              <FaTimes size={24} />
            </button>
          </div>
          <nav className="header-nav-mobile">
            <Link to="/" onClick={closeMenu} className="header-menu-link">Home</Link>
            <Link to="/seller" onClick={closeMenu} className="header-menu-link">Negozi</Link>
            <Link to="/categories" onClick={closeMenu} className="header-menu-link">Categorie</Link>
            <Link to="/brands" onClick={closeMenu} className="header-menu-link">Brand</Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;
